import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';

const NAV_STEPS = [
{
  id: 'assessment',
  label: 'Assessment',
  description: 'Share your professional background',
  path: '/professional-input-form',
  step: 1
},
{
  id: 'blueprint',
  label: 'Blueprint',
  description: 'Your personalized regenerative income strategy',
  path: '/prosperous-income-blueprint',
  step: 2
}];


const ProcessHeader = ({ completedSteps = [] }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hasPaid, setHasPaid] = useState(() => {
    return sessionStorage.getItem('quantume_blueprint_paid') === 'true';
  });

  useEffect(() => {
    const checkPayment = () => {
      setHasPaid(sessionStorage.getItem('quantume_blueprint_paid') === 'true');
    };
    window.addEventListener('storage', checkPayment);
    // Also poll for sessionStorage changes (same-tab updates)
    const interval = setInterval(checkPayment, 1000);
    return () => {
      window.removeEventListener('storage', checkPayment);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location?.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {document.body.style.overflow = '';};
  }, [mobileMenuOpen]);

  const getStepState = (step) => {
    if (completedSteps?.includes(step?.id)) return 'completed';
    if (location?.pathname === step?.path) return 'active';
    return 'default';
  };

  const currentStepIndex = NAV_STEPS?.findIndex((s) => s?.path === location?.pathname);

  return (
    <>
      <header
        className="process-header"
        style={{
          boxShadow: scrolled ? 'var(--shadow-md)' : 'var(--shadow-sm)',
          transition: 'box-shadow 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}
        role="banner">

        <div className="process-header-inner">
          {/* Logo */}
          <Link
            to="/professional-input-form"
            className="process-header-logo"
            aria-label="QuantuMe Prospectus - Go to Assessment">

            <img
              src="/assets/images/logo-prospectus-rocket-1774050332696.png"
              alt="QuantuMe Prospectus logo"
              className="h-9 w-auto object-contain" />

            <span style={{ fontFamily: 'Nexa Bold, Nexa, sans-serif', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-0.01em', lineHeight: 1 }}>
              <span style={{ color: '#283ba3' }} className="text-2xl">QuantuMe</span>
              <span style={{ color: '#d3ac35' }} className="text-2xl"> Prospectus</span>
            </span>
          </Link>

          {/* Desktop Progress Indicator */}
          <nav
            className="progress-indicator hidden md:flex"
            aria-label="Consultation process steps">

            {NAV_STEPS?.map((step, index) => {
              const state = getStepState(step);
              const isLocked = step?.id === 'blueprint' && !hasPaid;
              return (
                <React.Fragment key={step?.id}>
                  <span
                    className={`progress-step ${state}`}
                    aria-disabled={isLocked ? "true" : undefined}
                    aria-current={!isLocked && state === 'active' ? 'step' : undefined}
                    aria-label={`Step ${step?.step}: ${step?.label}${state === 'completed' ? ' (completed)' : state === 'active' ? ' (current)' : ''}`}
                    title={isLocked ? "Complete payment to access your Blueprint" : step?.description}
                    style={isLocked ? { cursor: 'not-allowed', opacity: 0.5, pointerEvents: 'none' } : undefined}>
                    <span className="progress-step-number" aria-hidden="true">
                      {state === 'completed' ?
                      <Icon name="Check" size={12} strokeWidth={3} /> :
                      step?.step
                      }
                    </span>
                    <span className="font-caption text-sm font-medium">{step?.label}</span>
                    {isLocked && <Icon name="Lock" size={12} strokeWidth={2} className="ml-1 opacity-60" />}
                  </span>
                  {index < NAV_STEPS?.length - 1 &&
                  <div
                    className={`progress-connector ${completedSteps?.includes(step?.id) ? 'completed' : ''}`}
                    aria-hidden="true" />

                  }
                </React.Fragment>
              );

            })}
          </nav>

          {/* Mobile Step Indicator */}
          <div className="flex md:hidden items-center gap-3">
            {currentStepIndex >= 0 &&
            <span
              className="font-caption text-sm text-muted-foreground"
              aria-live="polite">

                Step {currentStepIndex + 1} of {NAV_STEPS?.length}
              </span>
            }
            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              className="flex items-center justify-center w-10 h-10 rounded-md text-foreground hover:bg-muted transition-all duration-250"
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav-menu"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}>

              <Icon
                name={mobileMenuOpen ? 'X' : 'Menu'}
                size={22}
                color="currentColor"
                strokeWidth={2} />

            </button>
          </div>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen &&
      <div
        id="mobile-nav-menu"
        className="mobile-menu-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu">

          {/* Close button */}
          <button
          type="button"
          onClick={() => setMobileMenuOpen(false)}
          className="absolute top-4 right-4 flex items-center justify-center w-10 h-10 rounded-md text-foreground hover:bg-muted transition-all duration-250"
          aria-label="Close navigation menu">

            <Icon name="X" size={22} color="currentColor" strokeWidth={2} />
          </button>

          <div className="mb-8">
            <p className="font-caption text-xs uppercase tracking-widest text-muted-foreground mb-1">
              Your Journey
            </p>
            <h2 className="font-heading text-h4 text-foreground">Consultation Steps</h2>
          </div>

          <nav aria-label="Mobile consultation steps" className="flex flex-col gap-3">
            {NAV_STEPS?.map((step) => {
            const state = getStepState(step);
            const isLocked = step?.id === 'blueprint' && !hasPaid;
            return isLocked ? (
              <span
                key={step?.id}
                className={`flex items-center gap-4 p-4 rounded-md border transition-all duration-250 border-border text-muted-foreground`}
                style={{ cursor: 'not-allowed', opacity: 0.5 }}
                title="Complete payment to access your Blueprint">

                  <div
                  className="w-10 h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 transition-all duration-250 border-current"
                  aria-hidden="true">
                    <span className="font-caption text-sm font-semibold">{step?.step}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-caption text-base font-medium">{step?.label}</span>
                    <span className="font-caption text-xs opacity-70 mt-0.5">{step?.description}</span>
                  </div>
                  <Icon name="Lock" size={16} color="currentColor" className="ml-auto opacity-60" />
                </span>
            ) : (
              <Link
                key={step?.id}
                to={step?.path}
                className={`flex items-center gap-4 p-4 rounded-md border transition-all duration-250 ${
                state === 'active' ? 'border-primary bg-muted text-primary' :
                state === 'completed' ? 'border-success/30 bg-success/5 text-success' : 'border-border text-muted-foreground hover:border-primary/30 hover:text-primary hover:bg-muted'}`
                }
                aria-current={state === 'active' ? 'step' : undefined}
                onClick={() => setMobileMenuOpen(false)}>

                  <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 transition-all duration-250 ${
                  state === 'active' ? 'bg-primary border-primary text-primary-foreground' :
                  state === 'completed' ? 'bg-success border-success text-success-foreground' : 'border-current'}`
                  }
                  aria-hidden="true">

                    {state === 'completed' ?
                  <Icon name="Check" size={16} strokeWidth={3} /> :

                  <span className="font-caption text-sm font-semibold">{step?.step}</span>
                  }
                  </div>
                  <div className="flex flex-col">
                    <span className="font-caption text-base font-medium">{step?.label}</span>
                    <span className="font-caption text-xs opacity-70 mt-0.5">{step?.description}</span>
                  </div>
                  {state === 'active' &&
                <Icon name="ChevronRight" size={18} color="currentColor" className="ml-auto" />
                }
                </Link>);

          })}
          </nav>

          <div className="mt-auto pt-8 border-t border-border">
            <div className="flex items-center gap-3">
              <img
              src="/assets/images/logo-prospectus-rocket-1774050332696.png"
              alt="QuantuMe Prospectus logo"
              className="h-8 w-auto object-contain" />

              <span style={{ fontFamily: 'Nexa Bold, Nexa, sans-serif', fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em', lineHeight: 1 }}>
                <span style={{ color: '#283ba3' }}>QuantuMe</span>
                <span style={{ color: '#d3ac35' }}> Prospectus</span>
              </span>
            </div>
            <p className="font-caption text-xs text-muted-foreground mt-2">
              Transforming skills into regenerative income.
            </p>
          </div>
        </div>
      }
    </>
  );

};

export default ProcessHeader;